import React from 'react';
import { I18n } from 'react-redux-i18n';
import SvgLocation from '../../../../assets/images/icons/Location';
import SvgPhoneOutline from '../../../../assets/images/icons/PhoneOutline';
import SvgMail from '../../../../assets/images/icons/Mail';
import SvgDollar from '../../../../assets/images/icons/Dollar';
import SvgUtensils from '../../../../assets/images/icons/Utensils';
import SvgHotel from '../../../../assets/images/icons/Hotel';
import SvgAward from '../../../../assets/images/icons/Award';
import SvgWineGlass from '../../../../assets/images/icons/WineGlass';
import MichelinStarIcon from '../../../../assets/images/icons/MichelinStarIcon';

const C = {
  accent: '#e25d34',
  textDark: '#1F2A33',
  textMid: '#4A5868',
  textLight: '#8494A5',
  textFaint: '#A8B5C2',
  border: '#E3E8ED',
  borderLight: '#EEF1F4',
  bgLight: '#F4F6F8'
};

type InfoRowProps = {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
};

const InfoRow = ({ icon, label, value }: InfoRowProps) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 14,
      padding: '14px 0',
      borderBottom: `1px solid ${C.borderLight}`
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 8,
        background: C.bgLight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}
    >
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          color: C.textFaint,
          marginBottom: 3
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13.5,
          fontWeight: 500,
          lineHeight: 1.5,
          color: C.textDark
        }}
      >
        {value}
      </div>
    </div>
  </div>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.07em',
      color: C.textMid,
      padding: '22px 0 6px'
    }}
  >
    {children}
  </div>
);

type GeneralTabProps = {
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  phone: string | null;
  email?: string | null;
  michelin_stars?: number | null;
  ws_award?: string | null;
  menu_price_level?: string | null;
  cuisine_type?: string | null;
  hotel?: string | null;
};

export const General = ({
  latitude,
  longitude,
  address,
  postal_code,
  city,
  region,
  country,
  phone,
  email,
  michelin_stars,
  ws_award,
  menu_price_level,
  cuisine_type,
  hotel
}: GeneralTabProps) => {
  const fullAddress = [address, postal_code, city, region, country]
    .filter(Boolean)
    .join(', ');

  const addressValue =
    latitude && longitude ? (
      <a
        href={`https://maps.google.com/?ll=${latitude},${longitude}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: C.accent, textDecoration: 'none' }}
      >
        {fullAddress || '-'}
      </a>
    ) : (
      fullAddress || '-'
    );

  const michelinCount = michelin_stars || 0;

  return (
    <>
      <SectionTitle>
        {I18n.t('storePopup.general.contactInformation')}
      </SectionTitle>

      <InfoRow
        icon={<SvgLocation stroke={C.textLight} />}
        label={I18n.t('storePopup.general.address')}
        value={addressValue}
      />
      <InfoRow
        icon={<SvgPhoneOutline stroke={C.textLight} />}
        label={I18n.t('storePopup.general.phone')}
        value={
          phone ? (
            <a
              href={`tel:${phone}`}
              style={{ color: C.accent, textDecoration: 'none' }}
            >
              {phone}
            </a>
          ) : (
            <span style={{ color: C.textFaint }}>-</span>
          )
        }
      />
      <InfoRow
        icon={<SvgMail stroke={C.textLight} />}
        label={I18n.t('storePopup.general.email')}
        value={
          email ? (
            <a
              href={`mailto:${email}`}
              style={{ color: C.accent, textDecoration: 'none' }}
            >
              {email}
            </a>
          ) : (
            <span style={{ color: C.textFaint }}>-</span>
          )
        }
      />

      {(menu_price_level || cuisine_type || hotel) && (
        <>
          <SectionTitle>
            {I18n.t('storePopup.general.additionalInformation')}
          </SectionTitle>

          {menu_price_level && (
            <InfoRow
              icon={<SvgDollar stroke={C.textLight} />}
              label={I18n.t('storePopup.general.menuPriceLevel')}
              value={menu_price_level}
            />
          )}
          {cuisine_type && (
            <InfoRow
              icon={<SvgUtensils stroke={C.textLight} />}
              label={I18n.t('storePopup.general.cuisineType')}
              value={cuisine_type}
            />
          )}
          {hotel && (
            <InfoRow
              icon={<SvgHotel stroke={C.textLight} />}
              label={I18n.t('storePopup.general.hotel')}
              value={hotel}
            />
          )}
        </>
      )}

      <SectionTitle>{I18n.t('storePopup.general.awards')}</SectionTitle>

      <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
        {/* Michelin Card */}
        <div
          style={{
            flex: 1,
            padding: '18px 16px',
            borderRadius: 8,
            background: 'linear-gradient(145deg, #FFF4F4 0%, #FDEAEA 100%)',
            border: '1px solid rgba(228,0,43,0.15)'
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: 'rgba(228,0,43,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 12
            }}
          >
            <SvgAward stroke="#E4002B" />
          </div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              color: '#E4002B',
              marginBottom: 8
            }}
          >
            {I18n.t('storePopup.awards.michelinStars')}
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {[1, 2, 3].map((i) => (
              <MichelinStarIcon
                key={i}
                width={20}
                height={20}
                style={{
                  display: 'block',
                  color: i <= michelinCount ? '#E4002B' : 'rgba(228,0,43,0.2)'
                }}
              />
            ))}
          </div>
          <div
            style={{
              fontSize: 11,
              color: C.textFaint,
              fontWeight: 500,
              marginTop: 6
            }}
          >
            {michelinCount === 0
              ? 'No stars awarded'
              : `${michelinCount} star${michelinCount > 1 ? 's' : ''}`}
          </div>
        </div>

        {/* Wine Spectator Card */}
        <div
          style={{
            flex: 1,
            padding: '18px 16px',
            borderRadius: 8,
            background: 'linear-gradient(145deg, #F9F0F0 0%, #F2E2E2 100%)',
            border: '1px solid rgba(141,0,4,0.15)'
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: 'rgba(141,0,4,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 12
            }}
          >
            <SvgWineGlass stroke="#8D0004" />
          </div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              color: '#8D0004',
              marginBottom: 8
            }}
          >
            {I18n.t('storePopup.awards.wineSpectatorAward')}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: ws_award ? C.textDark : C.textFaint
            }}
          >
            {ws_award || '—'}
          </div>
          {!ws_award && (
            <div
              style={{
                fontSize: 11,
                color: C.textFaint,
                fontWeight: 500,
                marginTop: 4
              }}
            >
              No award listed
            </div>
          )}
        </div>
      </div>
    </>
  );
};
